import {
  OfferAiCatalogOptionDto,
  OfferAiDishSourcesDto,
  OfferAiDishSourcesSubgroupDto,
  OfferAiQuestionSubgroupDto,
} from '@/api/generated';

export const OFFER_AI_MAX_DISH_COUNT = 20;

export type PolishPluralForm = 'few' | 'many' | 'one';

export const polishPluralForm = (count: number): PolishPluralForm => {
  if (count === 1) {
    return 'one';
  }
  const lastTwo = Math.abs(count) % 100;
  const last = lastTwo % 10;
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? 'few' : 'many';
};

export const offerAiSubgroupPath = (groupName: string, subgroupName: string): string =>
  `${groupName} › ${subgroupName}`;

export const hasEventTypeDishes = (sources: OfferAiDishSourcesDto): boolean =>
  Boolean(sources.eventTypeName) &&
  sources.combinations.some(combination => combination.eventTypeDishCount > 0);

const within = (chosen: number[], id: number | undefined): boolean =>
  chosen.length === 0 || (id !== undefined && chosen.includes(id));

export const countMatchingDishes = (
  sources: OfferAiDishSourcesDto,
  departmentIds: number[],
  dishTypeIds: number[],
  byEventType: boolean,
): number =>
  sources.combinations
    .filter(combination => within(departmentIds, combination.departmentId))
    .filter(combination => within(dishTypeIds, combination.dishTypeId))
    .reduce(
      (sum, combination) =>
        sum + (byEventType ? combination.eventTypeDishCount : combination.dishCount),
      0,
    );

export type OfferAiSourceOption = {
  dishCount: number;
  id: number;
  name: string;
};

export const departmentOptions = (
  sources: OfferAiDishSourcesDto,
  selectedDepartmentIds: number[],
  selectedDishTypeIds: number[],
  byEventType: boolean,
): OfferAiSourceOption[] =>
  sourceOptions(
    sources.departments,
    selectedDepartmentIds,
    id => countMatchingDishes(sources, [id], [], byEventType),
    id => countMatchingDishes(sources, [id], selectedDishTypeIds, byEventType),
  );

export const dishTypeOptions = (
  sources: OfferAiDishSourcesDto,
  selectedDepartmentIds: number[],
  selectedDishTypeIds: number[],
  byEventType: boolean,
): OfferAiSourceOption[] =>
  sourceOptions(
    sources.dishTypes,
    selectedDishTypeIds,
    id => countMatchingDishes(sources, [], [id], byEventType),
    id => countMatchingDishes(sources, selectedDepartmentIds, [id], byEventType),
  );

const sourceOptions = (
  options: OfferAiCatalogOptionDto[],
  selectedIds: number[],
  totalOf: (id: number) => number,
  matchingOf: (id: number) => number,
): OfferAiSourceOption[] =>
  options
    .filter(option => selectedIds.includes(option.id) || totalOf(option.id) > 0)
    .map(option => ({ dishCount: matchingOf(option.id), id: option.id, name: option.name }));

export type OfferAiDishSourcesDraft = {
  departmentIds: number[];
  dishCount: number;
  dishTypeIds: number[];
};

type Words = {
  allDepartments: string;
  allDishTypes: string;
  dishes: (count: number) => string;
  header: string;
  wholeCatalog: (eventTypeName: string) => string;
};

const namesOf = (ids: number[], options: OfferAiCatalogOptionDto[], whenEmpty: string): string =>
  ids.length === 0
    ? whenEmpty
    : ids
        .map(id => options.find(option => option.id === id)?.name)
        .filter(Boolean)
        .join(', ');

export const dishSourcesAnswerPreview = (
  sources: OfferAiDishSourcesDto,
  kept: { draft: OfferAiDishSourcesDraft; subgroup: OfferAiDishSourcesSubgroupDto }[],
  ignoreEventType: boolean,
  words: Words,
): string => {
  const lines = kept.map(
    ({ draft, subgroup }) =>
      `• ${offerAiSubgroupPath(subgroup.groupName, subgroup.subgroupName)} — ${words.dishes(draft.dishCount)}` +
      ` · ${namesOf(draft.departmentIds, sources.departments, words.allDepartments)}` +
      ` · ${namesOf(draft.dishTypeIds, sources.dishTypes, words.allDishTypes)}`,
  );
  const lifted =
    ignoreEventType && sources.eventTypeName ? [words.wholeCatalog(sources.eventTypeName)] : [];
  return [words.header, ...lines, ...lifted].join('\n');
};

export const subgroupsAnswerPreview = (
  chosen: OfferAiQuestionSubgroupDto[],
  header: (list: string) => string,
): string =>
  header(
    chosen
      .map(subgroup => offerAiSubgroupPath(subgroup.groupName, subgroup.subgroupName))
      .join(', '),
  );
